import React, { useState } from "react";
import styles from "./FumigationContractorList.module.scss";
import { ATMField } from "../../../components/atoms/ATMField/ATMField.component";
import { ATMGrid } from "../../../components/atoms/ATMGrid/ATMGrid.component";
import { ATMInput } from "../../../components/atoms/ATMInput/ATMInput.component";
import { ATMForm } from "../../../components/atoms/ATMForm/ATMForm.component";
import { Box } from "@mui/material";
import { ATMDivider } from "components/atoms/ATMDivider/ATMDivider.component";
import FumigationContractorListConstants from "constants/FumigationContractorListConstants/FumigationContractorListConstants";
import { ATMButton } from "components/atoms/ATMButton/ATMButton.component";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ATMModal } from "../../../components/atoms/ATMModal/ATMModal.component";
import useInsertFumigationCompany from "hooks/useInsertFumigationCompany";
import useUpdateFumigationCompany from "hooks/useUpdateFumigationCompany";
import useSnackbar from "hooks/useSnackbar";
import { FumigationCompanyPayload } from "services/fumigationCompany.types";
import moment from "moment";


interface FumigationContractorListProps {
    control?: any;
    fumigationCompanyDetails?: any;
    onRefresh?: () => void;
}

const FumigationContractorList: React.FC<FumigationContractorListProps> = ({ control, fumigationCompanyDetails, onRefresh }) => {

    const [formValues, setFormValues] = useState({
        customerName: '',
        serviceAddress: '',
        aptOrSpaceFraction: '',
    });

    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
    const [nameError, setNameError] = useState<string>('');

    const { showSnackbar, snackbar } = useSnackbar();

    const { mutate: insertFumigationCompany, isLoading: isInserting } = useInsertFumigationCompany();
    const { mutate: updateFumigationCompany, isLoading: isUpdating } = useUpdateFumigationCompany();
    // Soft delete is performed via the PUT/update endpoint (sets status = 'DELETED').
    const { mutate: softDeleteFumigationCompany, isLoading: isDeleting } = useUpdateFumigationCompany();

    const isMutating = isInserting || isUpdating || isDeleting;

    const handleRowClick = (params: any) => {
        setSelectedRowId(params.row.fumCompanyNm);
        setNameError('');
        setFormValues({
            customerName: params.row.fumCompanyNm ?? '',
            serviceAddress: params.row.fumPhoneNbr ?? '',
            aptOrSpaceFraction: params.row.fumNotes ?? '',
        });
    };

    const handleClear = () => {
        setSelectedRowId(null);
        setNameError('');
        setFormValues({ customerName: '', serviceAddress: '', aptOrSpaceFraction: '' });
    };

    // Map the form fields to the API payload shape (see fumigationCompSchema on the server).
    const buildPayload = (): FumigationCompanyPayload => ({
        fumCompanyNm: formValues.customerName.trim(),
        fumPhoneNbr: formValues.serviceAddress.trim() || null,
        fumNotes: formValues.aptOrSpaceFraction.trim() || null,
    });

    const handleInsert = () => {
        const companyName = formValues.customerName.trim();
        
        if (!companyName) {
            setNameError('Fumigation Company Name is required.');
            return;
        }

        // Check if company already exists (case-insensitive)
        const existingCompany = fumigationCompanyDetails?.find(
            (company: any) => company.fumCompanyNm?.toLowerCase() === companyName.toLowerCase()
        );

        if (existingCompany) {
            setNameError(`Fumigation company "${companyName}" already exists.`);
            showSnackbar(`Company "${companyName}" already exists. Please use a different name.`, 'warning');
            return;
        }

        setNameError('');
        insertFumigationCompany(buildPayload(), {
            onSuccess: () => {
                showSnackbar('Fumigation company added.', 'success');
                onRefresh?.();
                handleClear();
            },
            onError: (error: any) => {
                console.error('Error inserting fumigation company:', error);
                // Check if the error is due to duplicate entry
                const errorMessage = error?.response?.data?.message || error?.message || '';
                if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('already exists')) {
                    setNameError(`Company "${companyName}" already exists in the database.`);
                    showSnackbar(`Failed to insert: Company already exists.`, 'error');
                } else {
                    showSnackbar('Failed to insert fumigation company.', 'error');
                }
            },
        });
    };

    const handleUpdate = () => {
        const fumCompanyNm = (selectedRowId ?? formValues.customerName).trim();
        if (!fumCompanyNm) {
            setNameError('Select a row or enter a Fumigation Company Name to update.');
            return;
        }

        // If updating with a different name, check if the new name already exists
        const newName = formValues.customerName.trim();
        if (selectedRowId && newName.toLowerCase() !== selectedRowId.toLowerCase()) {
            const existingCompany = fumigationCompanyDetails?.find(
                (company: any) => company.fumCompanyNm?.toLowerCase() === newName.toLowerCase()
            );

            if (existingCompany) {
                setNameError(`Cannot rename: Company "${newName}" already exists.`);
                showSnackbar(`Company "${newName}" already exists. Please use a different name.`, 'warning');
                return;
            }
        }

        setNameError('');
        updateFumigationCompany({ ...buildPayload(), fumCompanyNm }, {
            onSuccess: () => {
                showSnackbar('Fumigation company updated.', 'success');
                onRefresh?.();
            },
            onError: (error: any) => {
                console.error('Error updating fumigation company:', error);
                const errorMessage = error?.response?.data?.message || error?.message || '';
                if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('already exists')) {
                    setNameError(`Cannot update: Company name already exists.`);
                    showSnackbar('Failed to update: Company name already exists.', 'error');
                } else {
                    showSnackbar('Failed to update fumigation company.', 'error');
                }
            },
        });
    };

    const handleDelete = () => {
        const fumCompanyNm = (selectedRowId ?? formValues.customerName).trim();
        if (!fumCompanyNm) {
            setNameError('Select a row or enter a Fumigation Company Name to delete.');
            return;
        }
        setNameError('');
        setDeleteTarget(fumCompanyNm);
    };

    const confirmDelete = () => {
        if (!deleteTarget) {
            return;
        }
        // Soft delete: PUT with deleted flag so the server sets status = 'DELETED'.
        softDeleteFumigationCompany({ fumCompanyNm: deleteTarget, deleted: true }, {
            onSuccess: () => {
                showSnackbar('Fumigation company deleted.', 'success');
                onRefresh?.();
                handleClear();
            },
            onError: (error) => {
                console.error('Error deleting fumigation company:', error);
                showSnackbar('Failed to delete fumigation company.', 'error');
            },
            onSettled: () => {
                setDeleteTarget(null);
            },
        });
    };

    const summaryColumns: GridColDef[] = [
        { field: 'fumCompanyNm', headerName: `${FumigationContractorListConstants.COMPANY_NAME.label}`, width: 300, type: 'string', filterable: true, sortable: true, hideable: true, disableColumnMenu: true },
        { field: 'fumPhoneNbr', headerName: `${FumigationContractorListConstants.PHONE_NUMBER.label}`, width: 300, type: 'string', filterable: true, sortable: true, hideable: true, disableColumnMenu: true },
        { field: 'fumNotes', headerName: `${FumigationContractorListConstants.NOTES.label}`, width: 300, type: 'string', filterable: true, sortable: false, hideable: true, disableColumnMenu: true },
        { field: 'status', headerName: `${FumigationContractorListConstants.STATUS.label}`, width: 150, type: 'string', filterable: true, sortable: true, hideable: true, disableColumnMenu: true, valueGetter: (value: any) => value ?? '' },
        {
            field: 'updateDttm',
            headerName: `${FumigationContractorListConstants.LAST_UPDATED.label}`,
            width: 200,
            type: 'string',
            filterable: true,
            sortable: true,
            hideable: true,
            disableColumnMenu: true,
            valueFormatter: (value: any) => {
                if (!value) return '';
                const parsed = moment(value);
                return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm A') : value;
            },
        },


    ]



return (

    <ATMGrid className={styles.gridStyle}>

        {snackbar}

        {/* Delete Confirmation Modal */}
        <ATMModal
            open={!!deleteTarget}
            onClose={() => setDeleteTarget(null)}
            size="tiny"
        >
            <ATMModal.Header>Confirm Delete</ATMModal.Header>
            <ATMModal.Content>
                Delete "{deleteTarget}"? It will be moved to Deleted Fumigation Companies.
            </ATMModal.Content>
            <ATMModal.Actions>
                <ATMButton
                    content="Cancel"
                    basic
                    disabled={isDeleting}
                    onClick={() => setDeleteTarget(null)}
                />
                <ATMButton
                    content="Delete"
                    negative
                    loading={isDeleting}
                    disabled={isDeleting}
                    onClick={confirmDelete}
                />
            </ATMModal.Actions>
        </ATMModal>

        {/* Form Fields Section */}
        <ATMGrid.Row>
            <ATMGrid.Column
                computer={8}
                tablet={8}
                mobile={16}
                className="sempraMediumMarginBottom"
            >
                <ATMField
                    as={ATMInput}
                    name={FumigationContractorListConstants.COMPANY_NAME.label || ""}
                    placeholder={FumigationContractorListConstants.COMPANY_NAME?.subtext || ""}
                    label={FumigationContractorListConstants.COMPANY_NAME.label}
                    required={FumigationContractorListConstants.COMPANY_NAME.isRequired}
                    maxLength={100}
                    fluid
                    className={styles.fieldStyle}
                    autoComplete="off"
                    control={control}
                    error={nameError || undefined}
                    value={formValues.customerName}
                    onChange={(_e: any, { value }: any) => {
                        if (nameError) setNameError('');
                        setFormValues(prev => ({ ...prev, customerName: value }));
                    }}
                />
            </ATMGrid.Column>

            <ATMGrid.Column
                computer={8}
                tablet={8}
                mobile={16}
                className="sempraMediumMarginBottom"
            >
                <ATMField
                    as={ATMInput}
                    restrictAngularBrackets
                    name={FumigationContractorListConstants.PHONE_NUMBER.name || ""}
                    label={FumigationContractorListConstants.PHONE_NUMBER.label}
                    placeholder={FumigationContractorListConstants.PHONE_NUMBER.subtext}
                    required={FumigationContractorListConstants.PHONE_NUMBER.isRequired}
                    fluid
                    className={styles.fieldStyle}
                    autoComplete="off"
                    maxLength={15}
                    value={formValues.serviceAddress}
                    onChange={(_e: any, { value }: any) => setFormValues(prev => ({ ...prev, serviceAddress: value }))}
                />
            </ATMGrid.Column>
        </ATMGrid.Row>

        <ATMGrid.Row>
            <ATMGrid.Column
                computer={16}
                tablet={16}
                mobile={16}
                className="sempraMediumMarginBottom"
            >
                <ATMField
                    as={ATMForm.TextArea}
                    name={FumigationContractorListConstants.NOTES.label || ""}
                    placeholder={FumigationContractorListConstants.NOTES?.subtext || ""}
                    label={FumigationContractorListConstants.NOTES.label}
                    required={FumigationContractorListConstants.NOTES.isRequired}
                    rows={3}
                    maxLength={500}
                    fluid
                    className={styles.fieldStyle}
                    autoComplete="off"
                    control={control}
                    value={formValues.aptOrSpaceFraction}
                    onChange={(_e: any, { value }: any) => setFormValues(prev => ({ ...prev, aptOrSpaceFraction: value }))}
                />
            </ATMGrid.Column>
        </ATMGrid.Row>

        {/* Action Buttons Section */}
        <ATMGrid.Row>
            <ATMGrid.Column width={16}>
                <Box sx={{ display: 'flex', gap: 2, marginBottom: 2, marginTop: 1 }}>
                    <ATMButton
                        content="Insert"
                        size="small"
                        primary
                        loading={isInserting}
                        disabled={isMutating}
                        onClick={handleInsert}
                    />
                    <ATMButton
                        content="Update"
                        size="small"
                        secondary
                        loading={isUpdating}
                        disabled={isMutating}
                        onClick={handleUpdate}
                    />
                    <ATMButton
                        content="Delete"
                        size="small"
                        basic
                        loading={isDeleting}
                        disabled={isMutating}
                        onClick={handleDelete}
                        style={{
                            color: '#d32f2f',
                            borderColor: '#d32f2f',
                        }}
                    />
                    <ATMButton
                        content="Clear"
                        size="small"
                        loading={false}
                        disabled={isMutating}
                        onClick={handleClear}
                        style={{
                            backgroundColor: '#f5f5f5',
                            color: '#616161',
                            border: '1px solid #e0e0e0',
                        }}
                    />
                </Box>
            </ATMGrid.Column>
        </ATMGrid.Row>

        <ATMDivider />

        {/* Data Grid Section */}
        <ATMGrid.Row>
            <ATMGrid.Column width={16}>
                <DataGrid
                    //rows={dummyData}
                    rows={fumigationCompanyDetails || []}
                    getRowId={(row) => row.fumCompanyNm}
                    columns={summaryColumns}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 10 }
                        }
                    }}
                    pageSizeOptions={[5, 10, 25, 50]}
                    showToolbar
                    onRowClick={handleRowClick}
                    disableRowSelectionOnClick
                    filterMode="client"
                    disableDensitySelector={false}
                    disableColumnMenu={false}
                    sx={{
                        minHeight: 400,
                        '& .MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: 'normal',
                            lineHeight: '1.5',
                            overflowWrap: 'break-word',
                            fontWeight: 'bold',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            height: 'auto !important',
                            minHeight: '56px',
                            backgroundColor: '#f5f5f5',
                        },
                        '& .MuiDataGrid-row:nth-of-type(even)': {
                            backgroundColor: '#fafafa',
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            backgroundColor: '#ffffff',
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#e3f2fd',
                        },
                        '& .MuiDataGrid-row': {
                            cursor: 'pointer',
                        },
                        [`& .MuiDataGrid-row[data-id="${selectedRowId}"]`]: {
                            backgroundColor: '#bbdefb !important',
                        }
                    }}
                />
            </ATMGrid.Column>
        </ATMGrid.Row>

    </ATMGrid>


)

}

export { FumigationContractorList };